import {
  metaCacheLookup,
  type CacheEntry,
  type MetaCachePolicy,
  type MetaCacheState,
} from '../cache/metaCache.ts';
import type { VideoDetails } from '../model/video.ts';

/**
 * **R14, for the entity R14 was written about.** `GET /api/v1/auth/history` returns bare video ids, so
 * every row's title, author, duration and date costs one `/api/v1/videos/{id}` call — and those are
 * enormous: measured **89 KB, 90 KB and 262 KB** on three consecutive history rows, against
 * the ~305 KB originally measured for a multi-format video. A 40-row page hydrated eagerly is **4–10 MB**.
 *
 * **`?fields=` does not help.** `/api/v1/videos/{id}?fields=title,author,authorId,lengthSeconds,published`
 * answered 91 127 bytes where the untrimmed record was 91 136 — the whole thing either way. That is the
 * same finding 3b made for `/api/v1/channels/{ucid}` and 3c made for `/api/v1/auth/playlists`, so it is
 * now a property of the API rather than a quirk of one endpoint: **there is no cheap projection of any
 * record.** Which leaves the cache as the only lever, exactly as R14 says.
 *
 * **This module writes no new policy.** `cache/metaCache.ts` already owns LRU-on-last-use, the hard entry
 * cap, the render-then-refresh TTL and wholesale eviction on a version bump, as pure functions over a
 * value — 3b built it one slice early for channels. What is here is the *entry type* and the fold, which
 * is all that differs. Writing a second eviction rule for videos is the thing this file exists to avoid.
 *
 * The three rules of a lazily hydrated list, unchanged from `subscriptions/hydrate.ts` because they are
 * the same list problem with a bigger payload:
 *
 * 1. **A row renders without its metadata.** History's version of this is unusually strong — see
 *    {@link videoFramePath}: the thumbnail needs no request at all, so the grid is *complete* before
 *    hydration rather than merely present.
 * 2. **Only what is on screen is fetched**, and only what the cache does not hold.
 * 3. **A stale hit renders and refreshes**, never blanks.
 */

/** The schema version of a cached video row. Bumped → the whole cache is dropped (R3/R5). */
export const VIDEO_META_VERSION = 1;

/**
 * R14's `CachedVideoMeta`, verbatim in intent: **~250 bytes, not the 89–262 KB record it came from.**
 *
 * What a history row draws is a title, a byline, a duration and a date. What the other 99.8 % of the
 * response is: every format's URL and bitrate, the whole description twice, ninety recommendations,
 * `authorThumbnails`, storyboards and captions — none of which a grid row can display and all of which
 * would need re-fetching anyway, because signed media URLs expire in hours (§7).
 *
 * Two deliberate departures from R14's sketch, both from measurements this project already owns:
 *
 * - **`authorId` is nullable**, where R14 writes `authorId: string`. A collaboration video has
 *   `authorId: ""` and `authorUrl: "/channel/"` — a non-empty URL that routes nowhere — so an empty id is
 *   stored as `null` and the byline is *unlinked* rather than pointed at "Nothing here". The `authorUrl`
 *   half is deliberately not cached: it exists only to be parsed by `channelRouteOf`, and a cache holding
 *   a value whose only purpose is to be distrusted is a cache holding a trap.
 * - **No image URL of any kind**, which is R14's explicit rule and here costs nothing: see
 *   {@link videoFramePath}.
 */
export interface CachedVideoMeta extends CacheEntry {
  readonly title: string;
  readonly author: string;
  /** `null` for a video with no single author — see this interface's note. */
  readonly authorId: string | null;
  readonly authorVerified: boolean;
  /** `0` means "the record did not say", which `listingFacts` already refuses to render as `0:00`. */
  readonly lengthSeconds: number;
  readonly publishedEpochSec: number | null;
  readonly viewCount: number | null;
}

/** The cache as it is persisted. The platform serialises this and nothing else. */
export type VideoMetaCache = MetaCacheState<CachedVideoMeta>;

/** Reduce a fetched video record to the row, dropping the ~99.8 % a grid row cannot use. */
export function videoMetaFrom(video: VideoDetails, nowEpochSec: number): CachedVideoMeta {
  return {
    id: video.videoId,
    title: video.title,
    author: video.author,
    // Empty → `null`, so the collaboration case (`authorId: ""`) is an absence rather than a link to
    // `/channel/`. `parse/video.ts` already normalises this, but a cache is a schema and the intent is
    // worth being explicit about at the boundary.
    authorId: video.authorId === null || video.authorId === '' ? null : video.authorId,
    authorVerified: video.authorVerified,
    lengthSeconds: video.lengthSeconds,
    publishedEpochSec: video.publishedEpochSec,
    viewCount: video.viewCount,
    cachedAtEpochSec: nowEpochSec,
    lastSeenEpochSec: nowEpochSec,
  };
}

/**
 * The instance-relative path of a video's frame, derived from **the id alone**.
 *
 * This is what makes a History page possible at all, and it is the one place where an ID-only list is
 * cheaper than a hydrated one. §8 measured it, and this was re-confirmed directly: `/vi/{videoId}/…jpg`
 * is addressable from a bare id — `mqdefault.jpg` is 9–16 KB and 320×180, which is one of only three
 * declared sizes that is actually 16:9. So the grid draws thumbnails, badges and links for forty rows
 * **before a single hydration request**, and hydration only fills in words.
 *
 * Relative rather than absolute, for R14's "store no image URLs" rule: the caller resolves it against the
 * *current* instance at render time (`resolveInstanceUrl`), so nothing cached or derived here pins an
 * origin the reader may have moved away from. Note this is the strong form of the rule — a channel avatar
 * has to cache its path because no path is derivable from a UCID (`parse/subscriptions.ts`), while a video
 * frame needs no storage at all.
 *
 * **One caveat the caller cannot see from the URL**: for a well-shaped id with no video behind it — which
 * `POST /api/v1/auth/history/{id}` allows into a history — this path **never answers**. Not a 404: the
 * connection produces nothing, and `curl` timed out at 25 s on two consecutive tries. So a dead row's
 * image stays pending forever and `onError` never fires; what covers it is that hydration answers
 * `404 {"error":"Video unavailable"}` promptly and the row is then rendered as unavailable.
 */
export function videoFramePath(videoId: string, name: 'mqdefault' | 'maxresdefault' = 'mqdefault'): string {
  return `/vi/${encodeURIComponent(videoId)}/${name}.jpg`;
}

/**
 * One history row, hydrated or not — and the difference is visible in the type rather than inferred from
 * an empty title.
 */
export interface HistoryRow {
  readonly videoId: string;
  /** Instance-relative; the platform resolves it. Present on *every* row, hydrated or not. */
  readonly thumbnailPath: string;
  /** `null` until a record has been folded in. Drives a placeholder, never a spinner. */
  readonly meta: CachedVideoMeta | null;
  readonly hydrated: boolean;
  /**
   * The row's video does not exist — hydration answered `404`. A normal state rather than an error:
   * `POST` validates an id's shape and not its existence, and YouTube deletes videos that were watched.
   */
  readonly unavailable: boolean;
}

export interface HistoryRowsResult {
  readonly rows: readonly HistoryRow[];
  /**
   * The ids worth fetching *now* — visible misses plus visible stale hits, already deduplicated and
   * already intersected with `visible`, so a caller that fires one request per id is correct by
   * construction rather than by remembering to filter.
   */
  readonly fetch: readonly string[];
  /** How many rows are known dead, for the page's note. */
  readonly unavailable: number;
}

export interface HistoryRowsInput {
  /** The ids, in the order the instance returned them — most recently marked first. */
  readonly ids: readonly string[];
  readonly cache: VideoMetaCache;
  /**
   * Which rows are on screen. **Empty means "nothing yet", not "everything"** — the opposite default
   * would hydrate the whole page on first paint, which is the failure this module exists to prevent
   * wearing a lazy loader's name. At 89–262 KB a row that is 4–10 MB rather than 3b's 1.5 MB.
   */
  readonly visible: readonly string[];
  /** Requests already in flight, so a scroll that re-enters a row does not fetch it twice. */
  readonly inFlight?: readonly string[];
  /** Ids whose hydration answered `404`. Never re-requested; see {@link HistoryRow.unavailable}. */
  readonly unavailable?: readonly string[];
  /**
   * Ids whose hydration failed for any *other* reason — a transport failure, a `500`, the zero-byte
   * `200`. Not re-requested for the life of this list, and **not** marked unavailable: the row keeps its
   * thumbnail and its link, and only its title is missing.
   *
   * **This exists because of a measured loop.** Without it, a row that fails stays a visible cache miss,
   * so every *other* row's successful hydration changes the cache, recomputes this fold, and puts the
   * failed id back in `fetch` — a browser pass over 40 rows re-requested one of them repeatedly, and on a
   * page whose rows fail in bulk (the instance unwell) that is quadratic in the
   * page size. One attempt per row per visit is the same budget a row that succeeds gets; a reload is what
   * asks again, which is right for failures this endpoint has repeatedly proven are sometimes permanent
   * per video and sometimes not.
   */
  readonly failed?: readonly string[];
  readonly nowEpochSec: number;
  readonly policy?: MetaCachePolicy;
}

/**
 * Fold the cache into the id list and say what to fetch.
 *
 * The order is the instance's, untouched — most recently marked first. Re-sorting by anything (hydrated
 * first, title, date) would rearrange the list as the requests land, which is the most disorienting
 * possible way to render progress, and would also throw away the one ordering the endpoint actually
 * knows.
 *
 * **A dead row is never re-requested.** It is excluded from `fetch` even though it is a cache miss, which
 * is the one asymmetry against 3b: a channel that fails to load is retried on the next visit (2d measured
 * 2 of 60 channels failing *transiently*), while a `404 Video unavailable` is a durable fact about a
 * deleted video and re-asking on every scroll would be a request per row per pass, forever. The
 * distinction is drawn by the *caller*, which is what keeps this pure: only ids it has actually seen 404
 * for arrive here, so a transport failure stays retryable.
 */
export function historyRows(input: HistoryRowsInput): HistoryRowsResult {
  const lookup = metaCacheLookup(input.cache, input.ids, input.nowEpochSec, input.policy);
  const byId = new Map(lookup.hits.map((hit) => [hit.id, hit]));
  const unavailable = new Set(input.unavailable ?? []);

  const rows = input.ids.map((videoId): HistoryRow => {
    const meta = byId.get(videoId) ?? null;
    return {
      videoId,
      thumbnailPath: videoFramePath(videoId),
      meta,
      hydrated: meta !== null,
      unavailable: unavailable.has(videoId),
    };
  });

  const visible = new Set(input.visible);
  const inFlight = new Set(input.inFlight ?? []);
  const failed = new Set(input.failed ?? []);
  const wanted = new Set([...lookup.misses, ...lookup.stale.map((entry) => entry.id)]);

  return {
    rows,
    fetch: input.ids.filter(
      (id) =>
        wanted.has(id) &&
        visible.has(id) &&
        !inFlight.has(id) &&
        !unavailable.has(id) &&
        !failed.has(id),
    ),
    unavailable: rows.filter((row) => row.unavailable).length,
  };
}
